import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { Button } from "../../Components/ui/button";
import { Label } from "../../Components/ui/label";
import { Input } from "../../Components/ui/input";
import { Textarea } from "../../Components/ui/textarea";
import {
  useCreateReportMutation,
  useGetProfileQuery,
  useGetSparksQuery,
  useGetMetricBySparkQuery,
  useGetFundersQuery,
  useGetProgramsQuery,
  useGetFunderMetricsQuery,
} from "../../../redux/reducers/apiSlice";
import FullPageNexusLogoAnimation from "../../Reusable/FullPageNexusLogoAnimation/FullPageNexusLogoAnimation";
import * as Sentry from "@sentry/react";

const validationSchema = Yup.object({
  // Funder is now OPTIONAL – any organization should be able to create a report
  funderId: Yup.string().nullable(),
  startDate: Yup.date().required("Start date is required"),
  endDate: Yup.date()
    .required("End date is required")
    .min(Yup.ref("startDate"), "End date must be after start date"),
  metrics: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().required("Metric name is required"),
        value: Yup.string().required("Metric value is required"),
      })
    )
    .min(1, "At least one metric is required"),
  narrative: Yup.string(),
});

interface Metric {
  name: string;
  value: string;
  programTag?: string;
}

const CreateReport = () => {
  const navigate = useNavigate();
  const [createReport, { isLoading }] = useCreateReportMutation();
  const { data: profileData, isLoading: profileLoading } = useGetProfileQuery();
  const { data: sparks } = useGetSparksQuery();
  const { data: funders } = useGetFundersQuery();
  const [selectedSpark, setSelectedSpark] = useState<string>("");
  const [selectedFunder, setSelectedFunder] = useState<string>("");
  const { data: preDeterminedMetrics, isLoading: metricsLoading, error: metricsError } = useGetMetricBySparkQuery(selectedSpark, {
    skip: !selectedSpark,
  });
  const { data: funderMetrics } = useGetFunderMetricsQuery(selectedFunder, {
    skip: !selectedFunder,
  });
  const [metrics, setMetrics] = useState<Metric[]>([
    { name: "", value: "", programTag: "" },
  ]);

  // Derive a sensible default organizationId from the user's profile (if available)
  const organizationId =
    (profileData as any)?.currentOrganization?.id ||
    (profileData as any)?.organizations?.[0]?.id ||
    undefined;

  // Auto-apply REQUIRED funder-specific metrics when a funder is selected (e.g., The Black Fund)
  useEffect(() => {
    if (!selectedFunder || !funderMetrics || funderMetrics.length === 0) {
      return;
    }

    const requiredMetricNames = funderMetrics
      .filter((fm: any) => (fm.requirement_type || fm.requirementType) === "REQUIRED")
      .map((fm: any) => fm.metric_name || fm.metricName);

    setMetrics((prev) => {
      const existingNames = prev.map((m) => m.name);
      
      // Remove empty metrics (name is empty) when required metrics are being added
      // This prevents showing an empty input alongside required metrics
      const nonEmptyMetrics = prev.filter((m) => m.name.trim() !== "");
      
      // If we have required metrics to add, start fresh with only non-empty metrics
      // Otherwise keep the existing metrics (including empty ones for custom entry)
      let next = requiredMetricNames.length > 0 ? nonEmptyMetrics : [...prev];

      // Add all required metrics that aren't already present
      requiredMetricNames.forEach((name) => {
        if (!existingNames.includes(name)) {
          next.push({ name, value: "", programTag: "" });
        }
      });

      // If no metrics exist after adding required ones, add one empty for custom metrics
      if (next.length === 0) {
        next.push({ name: "", value: "", programTag: "" });
      }

      return next;
    });
  }, [selectedFunder, funderMetrics]);

  if (profileLoading) {
    return <FullPageNexusLogoAnimation />;
  }

  const handleAddMetric = () => {
    setMetrics([...metrics, { name: "", value: "" }]);
  };

  const handleAddPreDeterminedMetric = () => {
    if (preDeterminedMetrics && preDeterminedMetrics.length > 0) {
      // Add first available pre-determined metric
      const firstMetric = preDeterminedMetrics[0];
      setMetrics([...metrics, { name: firstMetric, value: "" }]);
    }
  };

  const handleRemoveMetric = (index: number) => {
    if (metrics.length > 1) {
      setMetrics(metrics.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (values: any) => {
    // First, check if all REQUIRED funder metrics have values
    if (funderMetrics && funderMetrics.length > 0) {
      const requiredMetrics = funderMetrics.filter(
        (fm: any) => (fm.requirement_type || fm.requirementType) === "REQUIRED"
      );
      
      const requiredMetricNames = requiredMetrics.map(
        (fm: any) => fm.metric_name || fm.metricName
      );
      
      // Check if all required metrics exist and have values
      const missingRequiredMetrics: string[] = [];
      requiredMetricNames.forEach((requiredName: string) => {
        const metric = metrics.find((m) => m.name === requiredName);
        if (!metric || !metric.value || metric.value.trim() === "") {
          missingRequiredMetrics.push(requiredName);
        }
      });
      
      if (missingRequiredMetrics.length > 0) {
        toast.error(
          `Please fill in all required funder metrics: ${missingRequiredMetrics.join(", ")}`,
          { duration: 6000 }
        );
        return;
      }
    }

    // Filter out metrics with empty names or values
    const validMetrics = metrics.filter(
      (m) => m.name.trim() !== "" && m.value.trim() !== ""
    );

    // Backend Joi schema currently expects metrics with only { name, value }
    // so we strip out programTag before sending, to avoid 400 '"metrics[0].programTag" is not allowed'
    const apiMetrics = validMetrics.map((m) => ({
      name: m.name,
      value: m.value,
    }));

    // Check if we have at least one valid metric
    if (apiMetrics.length === 0) {
      toast.error("Please add at least one metric with both name and value");
      return;
    }

    try {
      const payload = {
        funderId: values.funderId,
        // program is now an optional free-text tag per metric; keep programId for backward compatibility but optional
        programId: values.programId || undefined,
        startDate: values.startDate,
        endDate: values.endDate,
        metrics: apiMetrics,
        narrative: values.narrative || "",
        organizationId: organizationId || undefined,
      };

      const report = await createReport(payload).unwrap();
      toast.success("Report created successfully!");
      navigate(`/reports/${report.id}`);
    } catch (error: any) {
      Sentry.captureException(error);
      console.error("CreateReportError", error);
      
      // Better error handling with more details
      let errorMessage = "Failed to create report";
      
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.status === 400) {
        errorMessage = "Invalid data. Please check all fields and try again.";
      } else if (error?.status === 403) {
        errorMessage = error?.data?.message || "Access denied. Please check your permissions.";
      } else if (error?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      }
      
      toast.error(errorMessage, { duration: 5000 });
      
      // If it's a tier limit error, show upgrade prompt
      if (error?.status === 403 && (errorMessage.includes("Free tier limit") || errorMessage.includes("tier limit"))) {
        toast.error("Upgrade to paid tier to unlock unlimited features!", { duration: 5000 });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/")}
              className="flex-shrink-0 w-full sm:w-auto"
            >
              ← Back
            </Button>
            <h1 className="text-2xl sm:text-3xl font-semibold">Create Impact Report</h1>
          </div>
          
          <style>{`
            /* Date input styling for better calendar placement */
            input[type="date"] {
              position: relative;
            }
            input[type="date"]::-webkit-calendar-picker-indicator {
              position: absolute;
              right: 12px;
              top: 50%;
              transform: translateY(-50%);
              cursor: pointer;
              opacity: 0.7;
              padding: 4px;
              width: 18px;
              height: 18px;
            }
            input[type="date"]::-webkit-calendar-picker-indicator:hover {
              opacity: 1;
            }
            input[type="date"]::-webkit-inner-spin-button,
            input[type="date"]::-webkit-clear-button {
              display: none;
            }
            /* Firefox date input styling */
            input[type="date"]::-moz-calendar-picker-indicator {
              cursor: pointer;
              opacity: 0.7;
            }
            input[type="date"]::-moz-calendar-picker-indicator:hover {
              opacity: 1;
            }
            /* Ensure date inputs have consistent sizing */
            input[type="date"] {
              min-width: 0;
              width: 100%;
            }
            /* Better spacing between form elements */
            .space-y-5 > * + * {
              margin-top: 1.25rem;
            }
          `}</style>

          <Formik
            initialValues={{
              funderId: "",
              startDate: "",
              endDate: "",
              narrative: "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <Form className="space-y-5">
                {/* Funder Dropdown (optional) */}
                <div className="space-y-2">
                  <Label htmlFor="funderId">
                    Funder <span className="text-xs text-gray-500">(optional)</span>
                  </Label>
                  <select
                    id="funderId"
                    name="funderId"
                    value={formik.values.funderId}
                    onChange={(e) => {
                      formik.setFieldValue("funderId", e.target.value);
                      setSelectedFunder(e.target.value);
                    }}
                    onBlur={formik.handleBlur}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
                  >
                    <option value="">No funder / not funded by a foundation</option>
                    {funders?.map((funder: any) => (
                      <option key={funder.id} value={funder.id}>
                        {funder.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">
                    Optional: select a funder (like The Black Fund) if this report is for a specific funder. You can still create a report without selecting a funder.
                  </p>
                </div>

                {/* Program Tag (per metric, optional) is handled in the metrics section below */}

                {/* Date Range */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startDate">Start Date *</Label>
                    <div className="relative">
                      <Input
                        id="startDate"
                        name="startDate"
                        type="date"
                        value={formik.values.startDate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        required
                        className="h-10 w-full pr-10"
                        style={{ 
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    {formik.touched.startDate && formik.errors.startDate && (
                      <p className="text-sm text-red-600">
                        {formik.errors.startDate}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endDate">End Date *</Label>
                    <div className="relative">
                      <Input
                        id="endDate"
                        name="endDate"
                        type="date"
                        value={formik.values.endDate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        required
                        className="h-10 w-full pr-10"
                        style={{ 
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    {formik.touched.endDate && formik.errors.endDate && (
                      <p className="text-sm text-red-600">
                        {formik.errors.endDate}
                      </p>
                    )}
                  </div>
                </div>

                {/* Spark Selection */}
                <div className="space-y-2">
                  <Label htmlFor="spark">Select Spark (Focus Area) *</Label>
                  <select
                    id="spark"
                    value={selectedSpark}
                    onChange={(e) => setSelectedSpark(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a spark to see available metrics...</option>
                    {sparks?.map((spark: any) => (
                      <option key={spark.id} value={spark.id}>
                        {spark.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-600">
                    {selectedSpark 
                      ? "Pre-determined metrics will appear below after selection..."
                      : "Select a focus area to load 300+ pre-determined metrics for your impact reporting"}
                  </p>
                </div>

                {/* Metrics */}
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <Label>Impact Metrics *</Label>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddMetric}
                        className="text-sm w-full sm:w-auto"
                      >
                        + Add Custom Metric
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddPreDeterminedMetric}
                        disabled={!selectedSpark}
                        className="text-sm w-full sm:w-auto"
                      >
                        + Add Pre-determined Metric
                      </Button>
                    </div>
                  </div>
                  {metrics.map((metric, index) => {
                    const isPreDetermined = preDeterminedMetrics?.includes(metric.name);
                    const metricError =
                      formik.touched.metrics &&
                      formik.errors.metrics &&
                      Array.isArray(formik.errors.metrics) &&
                      (formik.errors.metrics as any)[index];

                    const isRequiredFunderMetric =
                      funderMetrics?.some(
                        (fm: any) =>
                          (fm.metric_name || fm.metricName) === metric.name &&
                          (fm.requirement_type || fm.requirementType) === 'REQUIRED'
                      ) || false;

                    return (
                      <div
                        key={index}
                        className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start"
                      >
                        <div className="md:col-span-4 space-y-2">
                          {isPreDetermined || isRequiredFunderMetric ? (
                            <div className="min-h-10 px-3 py-2 border border-gray-300 rounded-md bg-blue-50 flex items-center min-w-0">
                              <span className="text-sm text-blue-700 flex-1 min-w-0 break-words">
                                {metric.name}
                              </span>
                              <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-1 rounded flex-shrink-0 whitespace-nowrap">
                                Required
                              </span>
                            </div>
                          ) : (
                            <>
                              <Input
                                placeholder="Metric name (e.g., Students served)"
                                value={metric.name}
                                onChange={(e) => {
                                  const newMetrics = [...metrics];
                                  newMetrics[index].name = e.target.value;
                                  setMetrics(newMetrics);
                                }}
                                onBlur={() =>
                                  formik.setFieldTouched(`metrics.${index}.name`, true)
                                }
                                className="h-10"
                              />
                              {metricError && (metricError as any)?.name && (
                                <p className="text-sm text-red-600">
                                  {(metricError as any).name}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                        <div className="md:col-span-2 space-y-2">
                          <Input
                            placeholder="Value *"
                            value={metric.value}
                            onChange={(e) => {
                              const newMetrics = [...metrics];
                              newMetrics[index].value = e.target.value;
                              setMetrics(newMetrics);
                            }}
                            onBlur={() =>
                              formik.setFieldTouched(`metrics.${index}.value`, true)
                            }
                            className="h-10 w-full"
                            required
                          />
                          {metricError && (metricError as any)?.value && (
                            <p className="text-sm text-red-600">
                              {(metricError as any).value}
                            </p>
                          )}
                          {!metric.value && metric.name && (
                            <p className="text-xs text-amber-600">
                              ⚠️ Value is required for this metric
                            </p>
                          )}
                        </div>
                        <div className="md:col-span-5 space-y-2">
                          <div className="flex flex-col sm:flex-row gap-3 items-start">
                            <div className="flex-1 space-y-1 min-w-0 w-full sm:w-auto">
                              <Input
                                placeholder="Program (optional)"
                                value={metric.programTag || ''}
                                onChange={(e) => {
                                  const newMetrics = [...metrics];
                                  newMetrics[index].programTag = e.target.value;
                                  setMetrics(newMetrics);
                                }}
                                className="h-10 w-full"
                              />
                              <p className="text-xs text-gray-500 leading-relaxed break-words pr-2">
                                Optional: tag this metric to a program (e.g., "Youth Leadership")
                              </p>
                            </div>
                            {metrics.length > 1 && !isRequiredFunderMetric && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => handleRemoveMetric(index)}
                                className="text-red-600 hover:bg-orange-500 hover:text-white hover:border-orange-500 flex-shrink-0 mt-0 sm:mt-0 whitespace-nowrap w-full sm:w-auto transition-colors"
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Available Pre-determined Metrics */}
                {selectedSpark && (
                  <div className="space-y-2">
                    <Label>Available Pre-determined Metrics</Label>
                    {metricsLoading && (
                      <div className="p-3 text-sm text-gray-600 border border-gray-200 rounded-md bg-gray-50">
                        Loading metrics...
                      </div>
                    )}
                    {metricsError && (
                      <div className="p-3 text-sm text-red-600 border border-red-200 rounded-md bg-red-50">
                        Error loading metrics. Please try selecting a different Spark.
                      </div>
                    )}
                    {!metricsLoading && !metricsError && preDeterminedMetrics && preDeterminedMetrics.length > 0 && (
                      <>
                        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50">
                          <div className="space-y-1">
                            {preDeterminedMetrics.map((metricName, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => {
                                  setMetrics([...metrics, { name: metricName, value: "" }]);
                                }}
                                className="block w-full text-left text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                              >
                                • {metricName}
                              </button>
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">
                          Click on any metric above to add it to your report ({preDeterminedMetrics.length} metrics available)
                        </p>
                      </>
                    )}
                    {!metricsLoading && !metricsError && preDeterminedMetrics && preDeterminedMetrics.length === 0 && (
                      <div className="p-3 text-sm text-gray-600 border border-gray-200 rounded-md bg-gray-50">
                        No pre-determined metrics found for this Spark.
                      </div>
                    )}
                  </div>
                )}

                {/* Funder-Specific Metrics */}
                {selectedFunder && funderMetrics && funderMetrics.length > 0 && (
                  <div className="space-y-2">
                    <Label>Funder-Specific Metrics</Label>
                    {(() => {
                      // Only show RECOMMENDED metrics here.
                      // REQUIRED metrics are auto-applied above and should not be clickable again.
                      const optionalMetrics = funderMetrics.filter((fm: any) => {
                        const requirementType = fm.requirement_type || fm.requirementType;
                        return requirementType !== 'REQUIRED';
                      });

                      if (optionalMetrics.length === 0) {
                        return (
                          <p className="text-xs text-gray-500">
                            All mandatory funder metrics have been applied above. There are no additional recommended metrics to add.
                          </p>
                        );
                      }

                      return (
                        <>
                          <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50">
                            <div className="space-y-1">
                              {optionalMetrics.map((funderMetric: any, idx: number) => {
                                const requirementType = funderMetric.requirement_type || funderMetric.requirementType;
                                const metricName = funderMetric.metric_name || funderMetric.metricName;
                                const metricDesc = funderMetric.metric_description || funderMetric.metricDescription;

                                const alreadyAdded = metrics.some((m) => m.name === metricName);

                                return (
                                  <button
                                    key={idx}
                                    type="button"
                                    disabled={alreadyAdded}
                                    onClick={() => {
                                      if (!alreadyAdded) {
                                        setMetrics([...metrics, { name: metricName, value: "", programTag: "" }]);
                                      }
                                    }}
                                    className={`block w-full text-left text-sm px-2 py-1 rounded transition-colors ${
                                      alreadyAdded
                                        ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                                        : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                                    }`}
                                  >
                                    {alreadyAdded ? '✅ Added: ' : '🟢 Recommended: '}
                                    {metricName}
                                    {metricDesc && (
                                      <span className="text-xs text-gray-500 block ml-6">
                                        {metricDesc}
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">
                            Recommended funder-specific metrics do not count toward your free tier metric limits. Click to add them to your report.
                          </p>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Narrative */}
                <div className="space-y-2">
                  <Label htmlFor="narrative">Narrative</Label>
                  <Textarea
                    id="narrative"
                    name="narrative"
                    placeholder="Describe your impact, outcomes, and activities..."
                    rows={6}
                    value={formik.values.narrative}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-orange-500 hover:bg-orange-600 w-full sm:w-auto"
                    disabled={isLoading}
                  >
                    {isLoading ? "Creating..." : "Create Report"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default CreateReport;

